import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { ArrowLeft, Mail as MailIcon, Coins, Circle, Send, Eye, MousePointerClick, Clock } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";

export default function Inbox() {
  const brand = useBranding();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all"); // all | unread | offers | emails
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/inbox", {
        params: { unread: filter === "unread" },
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openItem = async (it) => {
    try {
      const { data } = await api.get(`/inbox/${it.id}`);
      setDetail({ ...it, ...data });
      if (!it.read) {
        try { await api.post(`/inbox/${it.id}/read`); } catch {}
        // Mark read locally AND update the header counter immediately.
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
        setCounts((c) => {
          const next = { ...c };
          if (it.kind === "offer") next.offers_unread = Math.max(0, (c.offers_unread || 0) - 1);
          if (it.kind === "email") next.emails_unread = Math.max(0, (c.emails_unread || 0) - 1);
          return next;
        });
      }
    } catch (e) {
      toast.error("Failed to open");
    }
  };

  const filtered = items.filter((i) => {
    if (filter === "offers") return i.kind === "offer";
    if (filter === "emails") return i.kind === "email";
    if (filter === "unread") return !i.read;
    return true;
  });

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="inbox-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="overline">{brand.brand_name}</span>
            <span className="text-[#D4D4D8]">/</span>
            <span className="text-sm font-medium">Inbox</span>
            <div className="ml-6 flex gap-4 text-[10px] font-mono uppercase tracking-widest text-[#525252]">
              <span>Offers · <span className="text-[#101828]">{counts.offers_total || 0}</span> · <span className="text-[#B45309]" data-testid="offers-new-count">{counts.offers_unread || 0} new</span></span>
              <span>Emails · <span className="text-[#101828]">{counts.emails_total || 0}</span> · <span className="text-[#B45309]" data-testid="emails-new-count">{counts.emails_unread || 0} new</span></span>
            </div>
          </div>
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-xs text-[#525252] hover:text-[#101828] font-mono uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Dashboard
          </Link>
        </header>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          {/* List — hidden on mobile when a detail is open */}
          <div className={`w-full md:w-[380px] md:border-r border-[#eaecf0] flex flex-col ${detail ? "hidden md:flex" : "flex"}`}>
            {counts.emails_total === 0 && (
              <div
                className="px-4 py-3 bg-[#EFF6FF] border-b border-[#DBEAFE] text-[11px] leading-relaxed"
                data-testid="mailgun-route-hint"
              >
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#1E40AF] mb-1">
                  Set up inbound
                </div>
                <div className="text-[#334155]">
                  In Mailgun → Routes, add:
                </div>
                <code className="block mt-1 font-mono text-[10px] bg-white p-2 rounded-sm border border-[#DBEAFE] break-all">
                  match_recipient(&quot;.*@yourdomain.com&quot;) → forward(&quot;{window.location.origin}/api/mail/inbound&quot;)
                </code>
              </div>
            )}
            <div className="px-4 py-2 border-b border-[#eaecf0] flex gap-1 text-[10px] font-mono uppercase tracking-widest">
              {["all", "unread", "offers", "emails"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-2 py-1 ${filter === f ? "bg-[#111111] text-white" : "text-[#525252] hover:bg-[#F4F4F5]"}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="p-6 font-mono text-xs text-[#525252]" data-testid="inbox-loading">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-6 font-mono text-xs text-[#525252]" data-testid="inbox-empty">No items yet.</div>
              ) : (
                filtered.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => openItem(it)}
                    data-testid={`inbox-item-${it.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-[#eaecf0] hover:bg-[#FAFAFA] ${detail?.id === it.id ? "bg-[#F4F4F5]" : ""} ${!it.read ? "border-l-2 border-l-[#03142f]" : ""}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {it.kind === "offer"
                        ? <Coins className="w-3 h-3 text-[#047857]" strokeWidth={1.75} />
                        : <MailIcon className="w-3 h-3 text-[#525252]" strokeWidth={1.75} />}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                        {new Date(it.at || Date.now()).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {!it.read && <Circle className="w-2 h-2 fill-[#03142f] text-[#03142f]" />}
                    </div>
                    <div className={`text-sm truncate ${!it.read ? "font-medium text-[#101828]" : "text-[#525252]"}`}>
                      {it.subject}
                    </div>
                    <div className="text-xs text-[#525252] truncate">
                      {it.name || it.from} — {it.preview || "(no body)"}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Detail — takes over the full screen on mobile */}
          <div className={`flex-1 overflow-auto p-6 md:p-8 ${detail ? "block" : "hidden md:block"}`}>
            {detail && (
              <button
                onClick={() => setDetail(null)}
                data-testid="mobile-back"
                className="md:hidden mb-4 text-xs font-mono uppercase tracking-widest text-[#525252]"
              >
                ← Back to list
              </button>
            )}
            {!detail ? (
              <div className="text-[#525252] font-mono text-xs" data-testid="inbox-nothing-selected">
                Select an item on the left.
              </div>
            ) : (
              <div data-testid="inbox-detail">
                <div className="overline text-[#525252] mb-1">
                  {detail.kind === "offer" ? "Offer" : "Email"} · {new Date(detail.at).toLocaleString()}
                </div>
                <h1 className="font-display text-2xl font-medium mb-4">{detail.subject}</h1>
                <div className="text-sm text-[#525252] mb-6">
                  <div>From: <span className="font-mono">{detail.from}</span></div>
                  {detail.kind === "offer" && (
                    <div className="mt-1">
                      Domain: <Link to={`/l/${detail.domain}`} target="_blank" className="font-mono text-[#03142f] hover:underline">{detail.domain}</Link>
                    </div>
                  )}
                </div>
                {detail.kind === "email" && detail.data?.body_html ? (
                  <div
                    className="prose prose-sm max-w-none border border-[#eaecf0] rounded-sm p-4 bg-white"
                    data-testid="detail-html"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(detail.data.body_html, {
                        FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
                        FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-mono text-xs text-[#111111] bg-[#F9F9F6] border border-[#eaecf0] rounded-sm p-4" data-testid="detail-text">
                    {detail.data?.body_text || detail.preview || detail.data?.message || "(no body)"}
                  </pre>
                )}
                {(detail.kind === "offer" || detail.kind === "email") && (
                  <ReplyPanel item={detail} />
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/* --------------------------- reply panel --------------------------- */

/** Built-in fallback presets — used when REPLY_TEMPLATES_JSON is blank or
 *  the admin's JSON is invalid. Keep in sync with the placeholder-help
 *  copy in Settings.jsx. */
const DEFAULT_REPLY_TEMPLATES = [
  {
    key: "accept",
    label: "Accept",
    color: "#12b76a",
    subject: "Re: {subject}",
    body:
      "Hi {name},\n\n" +
      "Thanks for the offer on {domain}. {amount} works — I'll send the invoice/escrow link shortly and push the domain to your account within 24 hours of confirmed payment.\n\n" +
      "Best,\n{broker}",
  },
  {
    key: "counter",
    label: "Counter",
    color: "#f79009",
    subject: "Re: {subject}",
    body:
      "Hi {name},\n\n" +
      "Thanks for the offer on {domain}. I can't quite meet {amount}, but I'd close today at {counter}. Deal is escrow-secured and the push completes within 24h.\n\n" +
      "Let me know if that works.\n\n" +
      "Best,\n{broker}",
  },
  {
    key: "reject",
    label: "Reject (politely)",
    color: "#f04438",
    subject: "Re: {subject}",
    body:
      "Hi {name},\n\n" +
      "Appreciate the offer on {domain}, but I'll have to pass at {amount} — it's under the floor I've set for this name. Feel free to circle back if you can stretch further.\n\n" +
      "Best,\n{broker}",
  },
];

function fillTemplate(tpl, ctx) {
  const map = {
    name: ctx.name || "there",
    domain: ctx.domain || "the domain",
    amount: ctx.amount || "your offer",
    counter: ctx.counter || "the counter figure",
    subject: ctx.subject || "your enquiry",
    broker: ctx.broker || "The broker",
  };
  const fill = (s) => s.replace(/\{(\w+)\}/g, (_, k) => map[k] ?? `{${k}}`);
  return { subject: fill(tpl.subject), body: fill(tpl.body) };
}

function ReplyPanel({ item }) {
  const [subject, setSubject] = useState(`Re: ${item.subject || ""}`);
  const [body, setBody] = useState("");
  const [counter, setCounter] = useState("");
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_REPLY_TEMPLATES);
  const brand = useBranding();

  // Pull admin-authored templates from Settings on mount. Falls back to the
  // built-ins on empty field or JSON parse error (with a soft warning).
  useEffect(() => {
    api.get("/settings").then((r) => {
      const raw = (r.data?.REPLY_TEMPLATES_JSON?.value || "").trim();
      if (!raw) { setTemplates(DEFAULT_REPLY_TEMPLATES); return; }
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0
            && parsed.every((t) => t?.key && t?.label && t?.body)) {
          setTemplates(parsed);
        } else {
          setTemplates(DEFAULT_REPLY_TEMPLATES);
        }
      } catch {
        toast.error("REPLY_TEMPLATES_JSON is not valid JSON — using defaults.");
        setTemplates(DEFAULT_REPLY_TEMPLATES);
      }
    }).catch(() => setTemplates(DEFAULT_REPLY_TEMPLATES));
  }, []);

  const applyTemplate = (tpl) => {
    const brokerName =
      [brand?.contact_first_name, brand?.contact_last_name].filter(Boolean).join(" ") ||
      brand?.brand_name || "The broker";
    const ctx = {
      name: (item.from_name || item.from || "").split("<")[0].trim() || (item.data?.name ?? "there"),
      domain: item.data?.domain || item.target_domain || item.subject?.split(" ").find((w) => w.includes(".") && w.length < 60) || "",
      amount: item.data?.amount != null
        ? `${(item.data.currency === "USD" ? "$" : "€")}${Number(item.data.amount).toLocaleString()}`
        : "your offer",
      counter: counter.trim() ? counter.trim() : "",
      subject: item.subject || "your enquiry",
      broker: brokerName,
    };
    const { subject: sub, body: bod } = fillTemplate(tpl, ctx);
    setSubject(sub);
    setBody(bod);
    toast.success(`${tpl.label} template applied — personalize the top line before sending.`);
  };

  const loadReplies = async () => {
    try {
      const { data } = await api.get(`/inbox/${item.id}/replies`);
      setReplies(data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadReplies(); }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Poll every 15s so an "opened just now" indicator lights up without a manual refresh.
    const iv = setInterval(loadReplies, 15000);
    return () => clearInterval(iv);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!body.trim() || !subject.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/inbox/${item.id}/reply`, { subject, body });
      if (data.ok) {
        toast.success("Reply sent");
        setBody("");
        loadReplies();
      } else {
        toast.error(data.reply?.send_error || "Send failed (check Mailgun setup)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8 border-t border-[#eaecf0] pt-6" data-testid="reply-panel">
      {replies.length > 0 && (
        <div className="mb-6">
          <div className="overline mb-3">Your replies · read receipts</div>
          <ul className="space-y-3">
            {replies.map((r) => <ReplyRow key={r.id} r={r} />)}
          </ul>
        </div>
      )}
      <div className="overline mb-3">Reply</div>
      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="reply-templates">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#98a2b3] mr-1">Templates:</span>
        {templates.map((tpl) => (
          <button
            key={tpl.key}
            type="button"
            onClick={() => applyTemplate(tpl)}
            data-testid={`reply-template-${tpl.key}`}
            className="cd-btn cd-btn--outline cd-btn--sm"
            style={{ borderColor: tpl.color || "var(--cd-gray-300)", color: tpl.color || "var(--cd-navy-950)", minHeight: 32, padding: "0 12px", fontSize: 12 }}
          >
            {tpl.label}
          </button>
        ))}
        <input
          value={counter}
          onChange={(e) => setCounter(e.target.value)}
          placeholder="Counter €"
          data-testid="reply-counter-value"
          className="cd-input"
          style={{ height: 32, width: 120, marginLeft: 4, fontSize: 12, padding: "0 10px" }}
        />
      </div>
      <input
        className="cd-input"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        data-testid="reply-subject"
        style={{ marginBottom: 10 }}
      />
      <textarea
        className="cd-input"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply…  HTML supported — every link becomes a click-tracked URL and a 1×1 open pixel is attached."
        data-testid="reply-body"
        style={{ height: 180, padding: 14 }}
      />
      <div className="flex items-center justify-between mt-3">
        <div className="text-[11px] font-mono text-[#98a2b3]">
          Sent to <span className="text-[#101828]">{item.from || "unknown"}</span> via Mailgun · open + click tracked
        </div>
        <button
          onClick={send}
          disabled={sending}
          className="cd-btn cd-btn--primary cd-btn--sm"
          data-testid="reply-send"
        >
          <Send size={13} strokeWidth={2} /> {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

function ReplyRow({ r }) {
  const opened = !!r.opened_at;
  const clicked = (r.click_count || 0) > 0;
  return (
    <li
      className="border border-[#eaecf0] rounded-md p-3"
      data-testid={`reply-row-${r.id}`}
      style={{ background: opened ? "#f0fdf4" : "#fafbfc" }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-medium text-[#101828] truncate max-w-md">{r.subject}</div>
        <div className="flex items-center gap-2 text-[11px] font-mono">
          {r.sent ? (
            <span style={{ color: "var(--cd-gray-500)" }}>
              <Clock size={10} strokeWidth={2} style={{ display: "inline", marginRight: 4 }} />
              {new Date(r.sent_at).toLocaleString()}
            </span>
          ) : (
            <span style={{ color: "var(--cd-danger)" }}>Send failed: {r.send_error}</span>
          )}
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
            style={{
              background: opened ? "#dcfce7" : "#f5f5f5",
              color: opened ? "#166534" : "#98a2b3",
            }}
            title={opened ? `Opened ${r.open_count}× · last at ${new Date(r.last_open_at || r.opened_at).toLocaleString()}` : "Not yet opened"}
            data-testid={`reply-open-${r.id}`}
          >
            <Eye size={10} strokeWidth={2} />
            {opened ? `Opened ${r.open_count || 1}×` : "Unread"}
          </span>
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
            style={{
              background: clicked ? "#dcfce7" : "#f5f5f5",
              color: clicked ? "#166534" : "#98a2b3",
            }}
            title={clicked ? `Clicked ${r.click_count}× · last at ${new Date(r.last_click_at).toLocaleString()}` : "No clicks"}
            data-testid={`reply-click-${r.id}`}
          >
            <MousePointerClick size={10} strokeWidth={2} />
            {clicked ? `${r.click_count} click${r.click_count === 1 ? "" : "s"}` : "0 clicks"}
          </span>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-[#475467] whitespace-pre-wrap font-mono">
        {(r.body_text || "").slice(0, 240)}{(r.body_text || "").length > 240 ? "…" : ""}
      </div>
    </li>
  );
}
